import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import EnhancedQueryDialog from "../../../components/Dialogs/EnhancedQueryDialog";
import ResourceColumnsChips from "../../../components/Organisms/ResourceColumnsChips";
import AvailableVariables from "../../../components/Molecules/Variables/AvailableVariables";
import QueryTemplatesCard from "../../../components/common/QueryTemplatesCard";
import {
  GENERIC_EXTERNAL_QUERY_TEMPLATES,
  GENERIC_RESOURCE_QUERY_TEMPLATES,
} from "../../../constants/queryTemplates";
import MixedSqlEditor from "./MixedSqlEditor";
import {
  IMixedSubQueryV2,
  buildResourceScopedColumns,
  buildExternalScopedColumns,
  buildMergeScopedColumns,
  buildMainQueryScopedColumns,
  MERGE_SUB_TYPES,
} from "./mixedQueryV2.utils";

// -1 targets the Main Query; >= 0 targets the sub-query at that index.
export interface DynamicSubQueryEditorDialogProps {
  open: boolean;
  onClose: () => void;
  // Which query is being edited: a sub-query index or -1 for the Main Query.
  targetIndex: number;
  subQueries: IMixedSubQueryV2[];
  columns: any;
  mainQuery: string;
  title: string;
  isReadOnly?: boolean;
  error?: any;
  // Commit the edited SQL back to the parent V2 state.
  onSaveSubQuery: (index: number, value: string) => void;
  onSaveMainQuery: (value: string) => void;
}

// Standalone, focused pop-out editor for a single sub-query or the Main Query.
// Composes the existing V2 building blocks (MixedSqlEditor + Available Columns
// chips + Available Variables) without touching the shared query dialogs. The
// SQL text is edited on a local draft copy and only committed on Save.
const DynamicSubQueryEditorDialog: React.FC<
  DynamicSubQueryEditorDialogProps
> = ({
  open,
  onClose,
  targetIndex,
  subQueries,
  columns,
  mainQuery,
  title,
  isReadOnly = false,
  error,
  onSaveSubQuery,
  onSaveMainQuery,
}) => {
  const editorRef = useRef<any>(null);
  const isMainQuery = targetIndex < 0;
  const item = !isMainQuery ? subQueries[targetIndex] : null;

  // Local draft: seeded on open so Cancel discards and Save commits.
  const [draft, setDraft] = useState<string>("");
  useEffect(() => {
    if (open) {
      setDraft(isMainQuery ? mainQuery || "" : item?.data_pull_query || "");
    }
    // Re-seed whenever the dialog opens for a different target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetIndex]);

  // Inserts text at the focused editor's cursor (columns / variables).
  const insertAtCursor = (text: string) => {
    const editor = editorRef?.current?.editor;
    if (!editor) return;
    const pos = editor.getCursorPosition();
    editor.session.insert(pos, text);
    editor.moveCursorToPosition({
      row: pos.row,
      column: pos.column + text.length,
    });
    editor.clearSelection();
    editor.focus();
  };

  const handleColumnSelect = (column: string) => insertAtCursor(`"${column}"`);
  const handleVariableSelect = (variable: string) =>
    insertAtCursor(`$$${variable}$$ `);

  // Detect sub_type to render the correctly-scoped Available Columns chips.
  const isExternal = item?.sub_type === "External";
  const isMerge = !!item?.sub_type && MERGE_SUB_TYPES.includes(item.sub_type);

  const scopedColumns = isMainQuery
    ? buildMainQueryScopedColumns(subQueries, columns)
    : isMerge
      ? buildMergeScopedColumns(item as IMixedSubQueryV2, columns)
      : isExternal
        ? buildExternalScopedColumns(item as IMixedSubQueryV2)
        : buildResourceScopedColumns(item as IMixedSubQueryV2, columns);

  const queryBuilderType = isMainQuery
    ? "main_query"
    : isMerge
      ? "merged_query"
      : isExternal
        ? "external_query"
        : "resource_query";

  const mergeQueryType = isMerge
    ? (item?.sub_type as string)
    : isExternal
      ? "External"
      : "Resource";

  const handleSave = () => {
    if (isMainQuery) {
      onSaveMainQuery(draft);
    } else {
      onSaveSubQuery(targetIndex, draft);
    }
    onClose();
  };

  const hasUnsavedChanges =
    draft !== (isMainQuery ? mainQuery || "" : item?.data_pull_query || "");

  return (
    <EnhancedQueryDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      hasUnsavedChanges={hasUnsavedChanges}
      confirmationTitle="Unsaved Query Changes"
      confirmationContent="You have unsaved query changes that will be lost. Are you sure you want to close?"
      className="mixed-v2-popout-dialog"
    >
      <Box className="mixed-v2-popout">
        <Box className="mixed-v2-popout__header">
          <Typography className="mixed-v2-popout__title">{title}</Typography>
        </Box>

        <Box className="mixed-v2-popout__body">
          <Box className="mixed-v2-popout__editor">
            <MixedSqlEditor
              value={draft}
              onChange={setDraft}
              editorStateKey={`mixed-v2-popout-${
                isMainQuery ? "main" : item?.id
              }`}
              label={title}
              error={error || {}}
              setErrors={() => null}
              showAvailableVariables={false}
              showResourceColumnsChips={false}
              editorRef={editorRef}
            />
          </Box>
          <Box className="mixed-v2-popout__side-panel">
            <Box className="available-box mixed-v2-popout__vars">
              <AvailableVariables
                error={error}
                setErrors={() => null}
                handleVariableSelect={handleVariableSelect}
              />
            </Box>
            <Box className="mixed-v2-popout__templates">
              <QueryTemplatesCard
                templates={[
                  ...GENERIC_RESOURCE_QUERY_TEMPLATES,
                  ...GENERIC_EXTERNAL_QUERY_TEMPLATES,
                ]}
                title="Query Templates"
              />
            </Box>
            <Box className="mixed-v2-popout__columns">
              <ResourceColumnsChips
                handleColumnSelect={handleColumnSelect}
                columns={scopedColumns}
                isFinalQuery={false}
                dialogType="mixed"
                queryBuilderType={queryBuilderType}
                mergeQueryType={mergeQueryType}
              />
            </Box>
          </Box>
        </Box>

        <Box className="mixed-v2-popout__footer">
          <Button
            variant="contained"
            color="secondary"
            className="btn-orange btn-border"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            className="btn-orange no-transform"
            onClick={handleSave}
            disabled={isReadOnly}
          >
            Save
          </Button>
        </Box>
      </Box>
    </EnhancedQueryDialog>
  );
};

export default DynamicSubQueryEditorDialog;
